/**
 * Export Section IDs - Canonical mapping for chart/table capture and PowerPoint export
 * 
 * 🖼️ STEP 4: Align Export Sections to UI Layout (Updated July 21)
 * These IDs match the actual UI components and PowerPoint slide placeholders
 */

export type ExportSectionId =
  | 'StrategicSummary'
  | 'FinancialSummary'
  | 'FiveYearProjection'
  | 'AnnualCumulativeSavings'
  | 'AutomationOpportunityAnalysis'
  | 'UpsidePotential';

/**
 * Section Export Configuration
 * Maps export section IDs to their properties for chart/table capture
 */
export interface ExportSectionConfig {
  id: ExportSectionId;
  displayName: string;
  powerpointPlaceholder: string;
  dataTestId: string;
  captureType: 'chart' | 'table' | 'combined' | 'text';
  isConditional?: boolean; // Whether section depends on data availability
  requiredData?: string[]; // Required data keys for section to be exportable
}

/**
 * Complete Export Section Registry - Updated with Canonical PowerPoint Export Identifiers
 * Defines all exportable sections with their capture configurations
 */
export const EXPORT_SECTIONS: Record<ExportSectionId, ExportSectionConfig> = {
  'StrategicSummary': {
    id: 'StrategicSummary',
    displayName: 'Strategic Summary',
    powerpointPlaceholder: 'StrategicSummary',
    dataTestId: 'strategic-summary-section',
    captureType: 'combined',
    isConditional: false
  },
  'FinancialSummary': {
    id: 'FinancialSummary',
    displayName: 'Financial Summary',
    powerpointPlaceholder: 'FinancialSummary',
    dataTestId: 'financial-summary-section',
    captureType: 'combined',
    isConditional: true,
    requiredData: ['pricingEnabled', 'roi_data']
  },
  'FiveYearProjection': {
    id: 'FiveYearProjection',
    displayName: 'Five-Year Projection',
    powerpointPlaceholder: 'FiveYearProjection',
    dataTestId: 'five-year-projection-chart',
    captureType: 'chart',
    isConditional: false
  },
  'AnnualCumulativeSavings': {
    id: 'AnnualCumulativeSavings',
    displayName: 'Annual & Cumulative Savings Forecast',
    powerpointPlaceholder: 'AnnualCumulativeSavings',
    dataTestId: 'value-forecast-chart',
    captureType: 'chart',
    isConditional: false
  },
  'AutomationOpportunityAnalysis': {
    id: 'AutomationOpportunityAnalysis',
    displayName: 'Automation Opportunity Analysis',
    powerpointPlaceholder: 'AutomationOpportunityAnalysis',
    dataTestId: 'weighted-automation',
    captureType: 'combined',
    isConditional: false
  },
  'UpsidePotential': {
    id: 'UpsidePotential',
    displayName: 'Upside Potential (Benchmark Gap Analysis)',
    powerpointPlaceholder: 'UpsidePotential',
    dataTestId: 'benchmark-gap-table',
    captureType: 'table',
    isConditional: false
  }
};

/**
 * Get exportable sections based on available data
 */
export function getExportableSections(valueSummary: any): ExportSectionConfig[] {
  return Object.values(EXPORT_SECTIONS).filter(section => {
    if (!section.isConditional) return true;
    
    // Check if required data is available
    if (section.requiredData) {
      return section.requiredData.every(dataKey => {
        const keys = dataKey.split('.');
        let current = valueSummary;
        
        for (const key of keys) {
          if (!current || !current[key]) return false;
          current = current[key];
        }
        
        return true;
      });
    }
    
    return true;
  });
}
